/**
 * Validation schemas using Yup
 * Centralized validation logic for forms
 */

// Note: This assumes Yup is installed. If not, we'll use a fallback validation approach
let yup = null

try {
  // Try to import Yup
  yup = require('yup')
} catch (error) {
  console.warn('Yup not installed. Using fallback validation.')
}

/**
 * Email validation schema
 */
export const emailSchema = yup ? yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters')
    .max(128, 'Password must be less than 128 characters')
}) : null

/**
 * Phone validation schema
 */
export const phoneSchema = yup ? yup.object({
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit phone number')
    .transform((value) => value.replace(/\D/g, '')), // Remove non-digits
  phone_country_code: yup
    .string()
    .required('Country code is required')
    .matches(/^\+[1-9]\d{0,3}$/, 'Please select a valid country code'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters')
    .max(128, 'Password must be less than 128 characters')
}) : null

// New schema for forms with country code
export const phoneWithCountryCodeSchema = yup ? yup.object({
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit phone number')
    .transform((value) => value.replace(/\D/g, '')), // Remove non-digits
  phone_country_code: yup
    .string()
    .required('Country code is required')
    .matches(/^\+[1-9]\d{0,3}$/, 'Please select a valid country code')
}) : null

/**
 * Fallback validation functions (used when Yup is not available)
 */
export const fallbackValidation = {
  email: (data) => {
    const errors = {}
    
    if (!data.email) {
      errors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      errors.email = 'Please enter a valid email address'
    } else if (data.email.length > 255) {
      errors.email = 'Email must be less than 255 characters'
    }
    
    if (!data.password) {
      errors.password = 'Password is required'
    } else if (data.password.length < 6) {
      errors.password = 'Password must be at least 6 characters'
    } else if (data.password.length > 128) {
      errors.password = 'Password must be less than 128 characters'
    }
    
    return { errors, isValid: Object.keys(errors).length === 0 }
  },
  
  phone: (data) => {
    const errors = {}
    
    if (!data.phone) {
      errors.phone = 'Phone number is required'
    } else {
      const cleanedPhone = data.phone.replace(/\D/g, '');
      if (!/^[0-9]{10}$/.test(cleanedPhone)) {
        errors.phone = 'Please enter a valid 10-digit phone number'
      }
    }
    
    // Validate country code if provided (optional for fallback, but should be present)
    if (data.phone_country_code && !/^\+[1-9]\d{0,3}$/.test(data.phone_country_code)) {
      errors.phone_country_code = 'Please select a valid country code'
    }
    
    if (!data.password) {
      errors.password = 'Password is required'
    } else if (data.password.length < 6) {
      errors.password = 'Password must be at least 6 characters'
    } else if (data.password.length > 128) {
      errors.password = 'Password must be less than 128 characters'
    }
    
    return { errors, isValid: Object.keys(errors).length === 0 }
  }
}

/**
 * Validate form data using appropriate schema
 * @param {string} loginType - 'email' or 'phone'
 * @param {Object} data - Form data to validate
 * @returns {Promise<Object>} - { errors, isValid }
 */
export const validateLoginForm = async (loginType, data) => {
  if (yup) {
    try {
      const schema = loginType === 'email' ? emailSchema : phoneSchema
      await schema.validate(data, { abortEarly: false })
      return { errors: {}, isValid: true }
    } catch (error) {
      const errors = {}
      error.inner.forEach((err) => {
        errors[err.path] = err.message
      })
      return { errors, isValid: false }
    }
  } else {
    // Use fallback validation
    const validator = loginType === 'email' ? fallbackValidation.email : fallbackValidation.phone
    return validator(data)
  }
}

/**
 * Format phone number for display
 * @param {string} phone - Raw phone number
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`
  }
  return phone
}

/**
 * Clean phone number (remove formatting)
 * @param {string} phone - Formatted phone number
 * @returns {string} - Clean phone number
 */
export const cleanPhoneNumber = (phone) => {
  return phone.replace(/\D/g, '')
}
